import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { tokens as c, iconHues } from '../theme/tokens';

// One Dashboard figure (Q15 / M13): a white tile with a 1-px line, the icon
// on a tinted circle, the label, then the number in tabular figures, dark
// ink on white so it reads on a dusty call-centre screen.
//
// `highlight` is kept for the one figure staff must act on (pending SOS): an
// orange outline, a pale orange fill and an orange number. Nothing else on
// the Dashboard is orange, so it stands out.
//
//   <DashboardCard icon={<PeopleAltOutlinedIcon />} title="Total customers" value="12,340" />
//   <DashboardCard icon={...} title="SOS reports" highlight
//     stats={[{ label: 'Total', value: 9 }, { label: 'Pending', value: 2, highlight: true }]} />
//
// stats: up to three small figures under the title ({ label, value, of?, highlight? });
// `of` adds a quiet "/ 1,200" after the value. loading: grey bars where the
// numbers go, the same size as the real ones.
//
// The tile measures itself (a CSS container): in a narrow tile, such as the
// two-column grid on a phone, the small figures stack as "label ... value"
// rows instead of three squeezed columns. Browsers without container queries
// keep the columns.
const NARROW = '@container (max-width: 220px)';

// The Dashboard's lifted surface (owner, 28 Oct): the same as its fleet health
// panels (FleetHealthCharts' Panel: theme shadow 6, 20-px corners, no line).
export const RAISED_SURFACE_SX = { border: 'none', boxShadow: 6, borderRadius: '20px' };

export default function DashboardCard({ icon, title, value, children, stats, caption, hue = 'accent', highlight = false, loading = false, raised = false, sx }) {
  const tone = iconHues[hue] || iconHues.accent;
  const main = value !== undefined ? value : children;
  return (
    <Paper
      component="section"
      sx={{
        height: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        minWidth: 0,
        containerType: 'inline-size',
        ...(raised ? RAISED_SURFACE_SX : {}),
        ...(highlight
          ? {
            bgcolor: c.ACCENT_TINT,
            borderColor: c.ACCENT,
            boxShadow: raised ? (theme) => `${theme.shadows[6]}, inset 0 0 0 1px ${c.ACCENT}` : `inset 0 0 0 1px ${c.ACCENT}`,
          }
          : {}),
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        {icon ? (
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: highlight ? c.WHITE : tone.bg,
              color: tone.fg,
              '& svg': { fontSize: 20 },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: c.INK_2, minWidth: 0, overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
      </Box>

      {main !== undefined && main !== null ? (
        <Typography
          variant="numeral"
          component="p"
          sx={{ m: 0, color: highlight && !stats ? c.ACCENT_TEXT : c.INK, overflowWrap: 'anywhere' }}
        >
          {loading ? <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: '6px' }} /> : main}
        </Typography>
      ) : null}

      {Array.isArray(stats) && stats.length ? (
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`,
            columnGap: 1.5,
            rowGap: 0.5,
            [NARROW]: { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 0.25 },
          }}
        >
          {stats.map((s) => (
            <Box
              key={s.label}
              sx={{
                minWidth: 0,
                // Narrow: "label ... value" on one row; when both do not fit,
                // the value moves under the label instead of breaking words.
                [NARROW]: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 },
              }}
            >
              <Typography component="dt" sx={{ fontSize: 12, lineHeight: 1.45, color: c.TEXT_3, overflowWrap: 'anywhere', [NARROW]: { overflowWrap: 'break-word' } }}>
                {s.label}
              </Typography>
              <Typography
                component="dd"
                sx={{
                  m: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  fontVariantNumeric: 'tabular-nums',
                  color: s.highlight ? c.ACCENT_TEXT : c.INK,
                  overflowWrap: 'anywhere',
                  [NARROW]: { ml: 'auto', whiteSpace: 'nowrap', overflowWrap: 'normal' },
                }}
              >
                {loading ? (
                  <Skeleton variant="rounded" width={44} height={20} sx={{ borderRadius: '6px', mt: 0.25 }} />
                ) : (
                  <>
                    {s.value}
                    {s.of !== undefined && s.of !== null ? (
                      <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: c.TEXT_3 }}>{` / ${s.of}`}</Box>
                    ) : null}
                  </>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {caption ? (
        <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: c.TEXT_3, overflowWrap: 'anywhere' }}>{caption}</Typography>
      ) : null}
    </Paper>
  );
}
